/**
 * Lo que falta por completar, con nombre y apellido.
 *
 * Antes esto eran unos chips con un número. Un número no dice qué hacer: hay
 * que poder ver qué guías son, qué pasa si se quedan así, e ir a arreglarlas.
 * Cada falta se abre y muestra sus folios, con enlace a la guía.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, TriangleAlert } from 'lucide-react';
import { Chip, Tarjeta, TarjetaTitulo } from '@/components/ui';
import { cn } from '@/lib/cn';
import { fecha as ffecha } from '@/domain/formato';
import { cuantasConPendientes, revisarPendientes, type GuiaRevisable } from '@/domain/pendientes';

/** Cuántos folios se listan antes de resumir el resto. */
const A_LA_VISTA = 12;

export function Pendientes({
  guias,
  titulo = 'Falta completar',
  vacio,
}: {
  guias: GuiaRevisable[];
  titulo?: string;
  /** Qué decir cuando no falta nada. Si no se pasa, la tarjeta no aparece. */
  vacio?: string;
}) {
  const [abierta, setAbierta] = useState<string | null>(null);
  const pendientes = revisarPendientes(guias);

  if (pendientes.length === 0) {
    if (!vacio) return null;
    return (
      <Tarjeta>
        <TarjetaTitulo accion={<Chip nivel="VERDE">Sin pendientes</Chip>}>{titulo}</TarjetaTitulo>
        <p className="p-4 text-xs text-texto-mute">{vacio}</p>
      </Tarjeta>
    );
  }

  const cuantas = cuantasConPendientes(pendientes);
  const deCobro = pendientes.filter((p) => p.gravedad === 'COBRO').length;

  return (
    <Tarjeta>
      <TarjetaTitulo
        accion={
          <Chip nivel={deCobro > 0 ? 'AMARILLO' : 'NEUTRO'}>
            {cuantas.toLocaleString('es-CL')} guía{cuantas === 1 ? '' : 's'}
          </Chip>
        }
      >
        {titulo}
      </TarjetaTitulo>

      <div className="divide-y divide-linea">
        {pendientes.map((p) => {
          const abierto = abierta === p.clave;
          return (
            <div key={p.clave}>
              <button
                type="button"
                onClick={() => setAbierta(abierto ? null : p.clave)}
                aria-expanded={abierto}
                className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5"
              >
                <TriangleAlert
                  size={15}
                  className={cn(
                    'mt-0.5 shrink-0',
                    p.gravedad === 'COBRO' ? 'text-fluor-amarillo' : 'text-texto-mute',
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    {p.titulo}
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-[11px] font-mono tabular-nums',
                        p.gravedad === 'COBRO'
                          ? 'bg-fluor-amarillo/15 text-fluor-amarillo'
                          : 'bg-white/5 text-texto-mute',
                      )}
                    >
                      {p.guias.length.toLocaleString('es-CL')}
                    </span>
                  </span>
                  <span className="mt-0.5 block text-xs text-texto-mute">{p.consecuencia}</span>
                </span>
                <ChevronDown
                  size={15}
                  className={cn(
                    'mt-0.5 shrink-0 text-texto-mute transition-transform',
                    abierto && 'rotate-180',
                  )}
                />
              </button>

              {abierto && (
                <div className="flex flex-wrap gap-1.5 px-4 pb-3 pl-11">
                  {p.guias.slice(0, A_LA_VISTA).map((g) => (
                    <Link
                      key={g.id}
                      to={`/guias/${g.id}`}
                      className="rounded-control border border-linea bg-petroleo-800/40 px-2 py-1 text-[11px] text-texto-mute transition-colors hover:bg-white/5 hover:text-texto"
                      title={`${g.plantaCodigo} · ${ffecha(g.fecha)}`}
                    >
                      <span className="font-mono">{g.folio}</span>{' '}
                      <span className="opacity-60">{ffecha(g.fecha)}</span>
                    </Link>
                  ))}
                  {p.guias.length > A_LA_VISTA && (
                    <span className="self-center text-[11px] text-texto-mute">
                      y {(p.guias.length - A_LA_VISTA).toLocaleString('es-CL')} más
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Tarjeta>
  );
}
